import { z } from 'zod';
import { SessionEventSchema, SessionId as SessionIdSchema, type SessionEvent } from '@/schemas';
import { useAuthStore } from '@/stores/auth-store';
import { useSessionStore } from '@/stores/session-store';

type BroadcastMechanism = {
  postMessage: (message: Record<string, unknown>) => void;
  cleanup?: () => void;
};

const globalObject: typeof globalThis | undefined =
  typeof globalThis === 'object' ? globalThis : undefined;

let fallbackTabIdCounter = 0;

const createFallbackTabId = (): string => {
  fallbackTabIdCounter += 1;
  return `tab-${Date.now().toString(16)}-${fallbackTabIdCounter.toString(16)}`;
};

const tabId = globalObject?.crypto?.randomUUID?.() ?? createFallbackTabId();

const STORAGE_EVENT_KEY = 'one-score:cross-tab-sync';

const SessionEventsMessageSchema = z.object({
  type: z.literal('session-events'),
  sessionId: SessionIdSchema,
  events: z.array(SessionEventSchema).min(1),
  originId: z.string(),
});

const AuthChangeMessageSchema = z.object({
  type: z.literal('auth-change'),
  userId: z.string().optional(),
  originId: z.string(),
});

const CrossTabMessageSchema = z.discriminatedUnion('type', [
  SessionEventsMessageSchema,
  AuthChangeMessageSchema,
]);

type CrossTabMessage = z.infer<typeof CrossTabMessageSchema>;

let mechanism: BroadcastMechanism | undefined;
let isInitialized = false;

function createBroadcastChannel(): BroadcastMechanism | undefined {
  if (!globalObject || typeof globalObject.BroadcastChannel !== 'function') {
    return undefined;
  }

  const channel = new globalObject.BroadcastChannel('one-score:cross-tab-sync');
  return {
    postMessage: (message) => {
      channel.postMessage(message);
    },
    cleanup: () => {
      channel.close();
    },
  };
}

function createStorageFallback(): BroadcastMechanism | undefined {
  const storage = globalObject?.localStorage;
  if (!globalObject || !storage) {
    return undefined;
  }

  return {
    postMessage: (message) => {
      try {
        const payload = JSON.stringify({ ...message, timestamp: Date.now() });
        storage.setItem(STORAGE_EVENT_KEY, payload);
        storage.removeItem(STORAGE_EVENT_KEY);
      } catch (error) {
        console.warn('[CrossTabSync] Failed to broadcast via localStorage', error);
      }
    },
  };
}

function ensureMechanism(): BroadcastMechanism | undefined {
  if (!mechanism) {
    mechanism = createBroadcastChannel() ?? createStorageFallback();
    if (!mechanism) {
      console.warn('[CrossTabSync] No cross-tab communication mechanism available');
    }
  }
  return mechanism;
}

function handleSessionEvents(message: z.infer<typeof SessionEventsMessageSchema>): void {
  if (message.originId === tabId) {
    return;
  }
  const sessionStore = useSessionStore.getState();
  void sessionStore
    .applyRemoteEvents(message.events)
    .catch((error) => console.warn('[CrossTabSync] Failed to apply remote events', error));
}

function handleAuthChange(message: z.infer<typeof AuthChangeMessageSchema>): void {
  if (message.originId === tabId) {
    return;
  }
  const authStore = useAuthStore.getState();
  void authStore
    .getCurrentUser()
    .catch((error) => console.warn('[CrossTabSync] Failed to refresh auth state', error));
}

function handleRawMessage(raw: unknown): void {
  const parseResult = CrossTabMessageSchema.safeParse(raw);
  if (!parseResult.success) {
    console.warn('[CrossTabSync] Ignoring malformed message', parseResult.error);
    return;
  }

  const message = parseResult.data;
  switch (message.type) {
    case 'session-events': {
      handleSessionEvents(message);
      break;
    }
    case 'auth-change': {
      handleAuthChange(message);
      break;
    }
    default: {
      // Exhaustive by zod discriminated union
      break;
    }
  }
}

function attachListeners(): void {
  if (!globalObject) {
    return;
  }

  const channelMechanism = ensureMechanism();

  if ('BroadcastChannel' in globalObject && channelMechanism?.cleanup) {
    const channel = new globalObject.BroadcastChannel('one-score:cross-tab-sync');
    const listener = (event: MessageEvent) => {
      handleRawMessage(event.data);
    };
    channel.addEventListener('message', listener);
    const previousCleanup = channelMechanism.cleanup;
    channelMechanism.cleanup = () => {
      channel.removeEventListener('message', listener);
      channel.close();
      previousCleanup?.();
    };
  } else if ('addEventListener' in globalObject && globalObject.addEventListener) {
    const storageListener = (event: StorageEvent) => {
      if (event.key !== STORAGE_EVENT_KEY || !event.newValue) {
        return;
      }
      try {
        const payload = JSON.parse(event.newValue) as unknown;
        handleRawMessage(payload);
      } catch (error) {
        console.warn('[CrossTabSync] Failed to parse storage message', error);
      }
    };
    globalObject.addEventListener('storage', storageListener);
    const previousCleanup = channelMechanism?.cleanup;
    mechanism = {
      postMessage: channelMechanism?.postMessage ?? (() => {}),
      cleanup: () => {
        globalObject.removeEventListener('storage', storageListener);
        previousCleanup?.();
      },
    };
  }
}

export function initCrossTabSync(): void {
  if (isInitialized) {
    return;
  }
  isInitialized = true;

  ensureMechanism();
  attachListeners();
}

export function broadcastSessionEvents(events: SessionEvent[]): void {
  if (events.length === 0) {
    return;
  }
  const sessionId = events[0]?.sessionId;
  if (!sessionId || !events.every((event) => event.sessionId === sessionId)) {
    console.warn('[CrossTabSync] Cannot broadcast events without consistent sessionId');
    return;
  }

  const payload: CrossTabMessage = {
    type: 'session-events',
    sessionId,
    events,
    originId: tabId,
  };

  ensureMechanism()?.postMessage(payload);
}

export function broadcastAuthChange(userId?: string): void {
  const payload: CrossTabMessage = {
    type: 'auth-change',
    userId,
    originId: tabId,
  };
  ensureMechanism()?.postMessage(payload);
}

export function teardownCrossTabSync(): void {
  mechanism?.cleanup?.();
  mechanism = undefined;
  isInitialized = false;
}
